import { cva } from 'class-variance-authority';

/**
 * Switch class recipes.
 *
 * Zero `dark:` variants, as everywhere else in this layer — colour comes entirely from the
 * `--ds-switch-*` slots that `.ds-switch` sets per theme in `components/styles/design-system.css`.
 *
 * ── The geometry, and why it is what it is ─────────────────────────────────────────────────────
 *
 * Read off the ten exported vectors of Figma node 593:39343, which are plain rects and circles
 * rather than the centred strokes the checkbox and radio are drawn with. Nothing had to be
 * translated between coordinate systems, so every number below is Figma's own:
 *
 *   track   36 x 18, `rx 9`      fully rounded, so `rounded-full` is exact
 *   thumb   `r 7`                14px across
 *   thumb   `cx 9` -> `cx 27`    2px inset at each end, and therefore 18px of travel
 *   halo    40 x 22, `rx 11`     the focused cells, 2px larger on every side
 *
 * ── The focus treatment is an OUTLINE, and the sibling components' is not ──────────────────────
 *
 * `Checkbox.recipe` argues at length for drawing its halo as a BACKGROUND on an oversized parent.
 * This one does the opposite on purpose, and the reasoning is worth keeping because "be consistent
 * with the checkbox" is the obvious review note:
 *
 *   1. The checkbox needs a background because its unchecked box is UNFILLED while focused, so the
 *      halo has to read through the box's interior. A switch track is opaque in every cell, so the
 *      only part of the halo that was ever visible is the 2px that sticks out — which is precisely
 *      what an outline paints. Behind and outside are indistinguishable here.
 *
 *   2. An outline is outside the box model, so it CANNOT affect layout. That is a structural
 *      guarantee rather than the checkbox's weaker "the parent happens to be big enough already",
 *      and the acceptance criterion on HNS-335 asks for exactly this.
 *
 *   3. It keeps the control's box at the authored 36x18. Had the root grown to 40x22 to hold a
 *      background, every settings row adopting this control would have silently gained 4px in both
 *      axes against the design's own `Examples` page.
 *
 * The rounded shape comes out right for free: an outline's radius is the border radius plus the
 * outline width, so 9px + 2px is the 11px Figma drew. `NothingMovesOnFocus` and `Geometry` in the
 * stories measure both halves of this.
 *
 * `focus-visible` rather than `focus`, so the ring answers a keyboard tab and not a mouse click —
 * the same call the checkbox and radio make, and for the same reason.
 *
 * ── WARNING: focus barely registers on an OFF switch ───────────────────────────────────────────
 *
 * The off track paints its resting colour in Enabled AND Focus — the two vectors differ only by the
 * halo. So focusing an off switch changes nothing but a 20%/50% tinted 2px ring, which does not
 * reach the 3:1 that WCAG 1.4.11 asks of a focus indicator. On an ON switch the track itself steps
 * Blue/Primary -> Blue/7, which does carry it.
 *
 * Reproduced as authored rather than quietly strengthened, exactly as `Radio.recipe` does with the
 * same problem: inventing a focus treatment pre-empts a designer who has demonstrably specified one
 * elsewhere. If the answer is "the off track should darken on focus too", it is one selector — the
 * hover rule below is the shape it would take.
 *
 * Hover, by contrast, WAS added by design after first review (the set went from ten vectors to
 * twelve): an off switch now steps one down the Light ramp, and dark mode sits one step darker than
 * light throughout. The earlier revision had no off-hover cell at all.
 *
 * ── How the states are selected ────────────────────────────────────────────────────────────────
 *
 * `group/sw` sits on `Switch.Root`, which carries `data-checked` / `data-unchecked` /
 * `data-disabled` and is also the focusable element, so one group serves everything and no colour
 * change is driven by React state.
 *
 * Selectors are MUTUALLY EXCLUSIVE rather than relying on utility sort order, the discipline the
 * three sibling recipes use. That matters more than it looks here: `:not()` takes the specificity
 * of its argument, so a `group-[[data-checked]:not([data-disabled])]` rule out-specifies a plain
 * `group-data-[disabled]` one by a wide margin. Every checked rule therefore carries an explicit
 * `:not([data-disabled])` and the disabled rule needs no counterpart. Precedence, highest first:
 *
 *   disabled -> on+hover/focus -> on -> off
 *
 * with off as the unselectored base case, since it is the one state that never varies.
 */

/**
 * The wrapper, which is a real `<label>`.
 *
 * `cursor` is set here and inherited by everything inside, so the track and the text agree — a
 * switch whose label is a text cursor and whose track is a pointer looks broken.
 *
 * `items-start`, not the `items-center` Figma authors: a label long enough to wrap must leave the
 * switch on the first line rather than dragging it down to the centre of the whole text block. The
 * checkbox and radio made the same call. What keeps a single-line switch rendering exactly as Figma
 * draws it is `switchAlignRecipe` below.
 */
export const switchRootRecipe = cva(['ds-switch', 'inline-flex w-fit items-start gap-[8px]'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * A 20px-tall shim that centres the 18px track on the first line of label text.
 *
 * This is the switch's version of the 2px the checkbox pads its LABEL with, inverted: there the
 * control (24px) was taller than the line box (20px) and the text had to grow to meet it; here the
 * track (18px) is shorter, so the control side is the one that has to grow.
 *
 * Doing it with a shim rather than a 1px margin on the track is what keeps `Switch.Root` itself
 * exactly 36x18 — which `Geometry` asserts, and which the outline above depends on for its radius.
 * It also means an unlabelled switch is 20px tall rather than 19px with a stray pixel at the top.
 */
export const switchAlignRecipe = cva(['flex h-[20px] shrink-0 items-center']);

/**
 * `Switch.Root` — the track, the hit target, the focus ring, and the group every other recipe reads.
 *
 * `outline-none` in the base is not redundant with the `focus-visible` rule below it: it replaces
 * the UA ring on a plain `:focus` (a mouse click) while leaving the authored one on a keyboard tab.
 *
 * NOTE the hit target is the authored 36x18 and nothing more, which is well under the 44px touch
 * guidance. Reproduced as drawn — HNS-335 raises it as an open question and the design has no
 * answer yet. A labelled switch is better off than that number suggests, since the whole `<label>`
 * is clickable; an unlabelled one in a settings row is not.
 */
export const switchControlRecipe = cva([
  'group/sw',
  /**
   * `rounded-[9px]`, not `rounded-full`. Both draw the same pill on an 18px-tall box, but Tailwind
   * compiles `rounded-full` to `calc(infinity * 1px)`, and two things follow from that:
   *
   *   - `getComputedStyle` reports the SPECIFIED radius, so it reads back as Chromium's clamp of
   *     infinity (33554432px) rather than as a number `Geometry` can assert against;
   *   - an outline's radius is the border radius plus its width, and infinity plus two is still
   *     infinity — so the focus ring's corner would be whatever the browser clamps to rather than
   *     the 11px Figma drew.
   *
   * 9 is also what the file actually says (`rx 9`), so this is the more faithful of the two.
   */
  'relative block h-[18px] w-[36px] shrink-0 cursor-[inherit] rounded-[9px]',

  /**
   * The outline COLOUR is set unconditionally and only its style is toggled, which looks redundant
   * and is load-bearing.
   *
   * Written the obvious way — colour under `focus-visible:` alongside the style — tabbing to the
   * switch flashed a dark ring for about a sixth of a second before it turned blue. `outline-none`
   * sets `outline-style: none` and leaves `outline-color` at its initial `currentColor`, which on
   * this element inherits the page's near-black text colour. Tailwind v4's `transition-colors`
   * includes `outline-color`. So focus was interpolating black -> halo blue over the full 150ms,
   * and the black end of that ramp is what you saw.
   *
   * Declaring the colour up front means there is nothing to interpolate: the ring is the right
   * colour before it is ever visible, and `outline-style` is not an animatable property, so it
   * appears at full strength immediately.
   *
   * The transition below is narrowed to `background-color` for the same reason — it is the only
   * thing on the track that should ever animate, and naming it explicitly stops a future
   * `transition-colors` from quietly re-animating the outline.
   */
  'outline-[color:var(--ds-switch-halo)] outline-offset-0',
  'outline-none focus-visible:outline-[2px] focus-visible:outline-solid',
  'transition-[background-color] duration-150 motion-reduce:transition-none',

  /**
   * These are `[&...]` self-selectors and NOT the `group-[...]/sw` form the sibling recipes use,
   * which is the one genuinely easy mistake to make in this file.
   *
   * `group-[…]` compiles to a DESCENDANT selector — `.group\/sw[data-checked] .target`. That is
   * right for the thumb, which is a child, and silently wrong here, because the track IS the group
   * element rather than something inside it. Written that way, not one of these rules ever matches:
   * every switch renders in its resting off colour, on and disabled included. Nothing catches it
   * either — happy-dom applies no CSS, and the interaction tests assert state and geometry rather
   * than paint. It took looking at the thing to see it, which is what the screenshot pass is for.
   *
   * `data-part="track"` would work as a hook too, but `&` says "this element" without inventing a
   * second name for it.
   */

  // Off, resting — and off + focused, which paints the same. The base case; everything below
  // out-specifies it.
  'bg-[var(--ds-switch-track)]',

  // Off, hovered. One step down the Light ramp, and the step itself is theme-conditional: Light/3 ->
  // Light/4 in light mode, Light/4 -> Light/5 in dark. Both ends live in the token layer, so this
  // is one rule rather than two.
  '[&[data-unchecked]:not([data-disabled]):hover]:bg-[var(--ds-switch-track-hover)]',

  // On, resting.
  '[&[data-checked]:not([data-disabled]):not(:hover):not(:focus-visible)]:bg-[var(--ds-switch-track-on)]',

  // On, hovered or focused — one step down the blue ramp, and the same step for both.
  '[&[data-checked]:not([data-disabled]):hover]:bg-[var(--ds-switch-track-on-hover)]',
  '[&[data-checked]:not([data-disabled]):focus-visible]:bg-[var(--ds-switch-track-on-hover)]',

  // Disabled, on or off — one grey for both, which is how the design draws it.
  '[&[data-disabled]]:bg-[var(--ds-switch-track-disabled)]',
]);

/**
 * `Switch.Thumb` — the knob, and the only thing in the component that moves.
 *
 * Positioned at the off end and translated to the on end rather than having its `left` animated:
 * a transform is composited, so the travel cannot trigger layout however many switches are on the
 * page, and it keeps the two end positions as one declaration each.
 *
 * `18px` is `36 - 2 - 14 - 2` — the track's width less the thumb and its inset at either end.
 *
 * `motion-reduce:transition-none` is what satisfies the reduced-motion criterion: the thumb still
 * arrives at the right end, it simply gets there in one frame.
 */
export const switchThumbRecipe = cva([
  // `rounded-[7px]` rather than `rounded-full` for the same reason as the track, and because `r 7`
  // on a 14px box is exactly what the vector draws.
  'pointer-events-none absolute top-[2px] left-[2px] size-[14px] rounded-[7px]',
  'bg-[var(--ds-switch-thumb)]',
  'group-data-[disabled]/sw:bg-[var(--ds-switch-thumb-disabled)]',
  'transition-[translate,background-color] duration-150 motion-reduce:transition-none',
  'group-data-[checked]/sw:translate-x-[18px]',
]);

/**
 * The label text.
 *
 * Both colours are variants rather than a base plus an override: two bare `text-*` utilities have
 * the same specificity, so which one won would come down to Tailwind's output order rather than
 * anything written here. Same reasoning as the checkbox and radio.
 */
export const switchLabelRecipe = cva(['font-sans text-[14px] leading-5 select-none'], {
  variants: {
    disabled: {
      true: 'text-[var(--ds-switch-label-disabled)]',
      false: 'text-[var(--ds-switch-label)]',
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * The label-LEFT settings row, applied to the `<label>` wrapper via `rootClassName`.
 *
 * Built by REVERSING this component's own row rather than composing a new one: `switchRootRecipe`
 * is `inline-flex w-fit items-start gap-[8px]`, so `flex-row-reverse` moves the label to the front
 * and `w-full justify-between` pushes the control out to the right edge. `cn` is `twMerge`, so
 * `w-full` beats the recipe's `w-fit` rather than fighting it in source order.
 *
 * Doing it this way keeps the label's size, colour and disabled treatment coming from
 * `switchLabelRecipe` instead of a copy that could drift — and keeps the native `<label>`
 * association, so the control is named without a generated id or an `aria-labelledby`.
 *
 * A plain string rather than a `cva` recipe because it has no axes: it is one layout, and the only
 * question a caller has is whether they want it. Lives here rather than privately in a wrapper
 * because it encodes how THIS component's row is put together, and two wrappers now ask for it —
 * `ReactHookForm/Switch` for the eight call sites passing `labelPlacement="start"`, and the
 * broadcast chat's moderation row.
 */
export const switchRowClass = 'w-full flex-row-reverse justify-between';
